import "./ghost-mansion.css";
import { paintManorRoom, paintSconceFlames } from "./ghost-mansion-art";
import type { SpectralManorResult } from "../worker/src/standalone-leaderboard";

type Role = "hunter" | "ghost";
type MansionVoiceCue =
  | "spectral-manor:lights-out"
  | "spectral-manor:hunt-begins"
  | "spectral-manor:lantern-lost"
  | "spectral-manor:lantern-relit"
  | "spectral-manor:hunters-win"
  | "spectral-manor:phantom-win";

type MansionVoiceOptions = {
  playerName?: string;
  onResult?: (result: SpectralManorResult) => Promise<boolean>;
  voicesEnabled: boolean;
  onVoicesEnabledChange: (enabled: boolean) => void;
  playVoiceover: (cue: MansionVoiceCue) => void;
  stopVoiceover: () => void;
};
type Phase = "setup" | "countdown" | "playing" | "paused" | "finished";
type Vec = { x: number; y: number };
type Rect = { x: number; y: number; w: number; h: number; kind: string };
type BatteryPickup = Vec & { id: number; phase: number };
type Hunter = Vec & {
  id: number;
  color: string;
  facing: number;
  health: number;
  battery: number;
  human: boolean;
  down: boolean;
  revive: number;
  pulse: number;
  flashlightOn: boolean;
  aiTarget: Vec;
  aiTimer: number;
  ghostLastSeen: Vec | null;
  ghostMemory: number;
};

const W = 900;
const H = 1600;
const TAU = Math.PI * 2;
const FOCUSED_BEAM_LENGTH = 170;
const FOCUSED_BEAM_SPREAD = .24;
const GUIDE_BEAM_LENGTH = 105;
const GUIDE_BEAM_SPREAD = .17;
const FLASHLIGHT_DRAIN = 34;
const FLASHLIGHT_RECOVERY = 4;
const BATTERY_RESTORE = 60;
const MAX_BATTERY_PICKUPS = 2;
const hunterColors = ["#6cf5ef", "#ffcf5c", "#ff7795", "#a991ff"];
const roomNames = [
  ["MOONLIT CONSERVATORY", "THE OLD LIBRARY"],
  ["THE GRAND FOYER", "PORTRAIT GALLERY"],
  ["THE CRIMSON PARLOR", "CANDLELIT DINING"],
];

const furniture: Rect[] = [
  { x: 105, y: 145, w: 145, h: 48, kind: "bookcase" },
  { x: 275, y: 395, w: 92, h: 76, kind: "piano" },
  { x: 535, y: 170, w: 190, h: 58, kind: "table" },
  { x: 748, y: 360, w: 58, h: 100, kind: "statue" },
  { x: 92, y: 675, w: 112, h: 72, kind: "sofa" },
  { x: 268, y: 865, w: 142, h: 60, kind: "table" },
  { x: 510, y: 692, w: 112, h: 72, kind: "sofa" },
  { x: 650, y: 882, w: 150, h: 52, kind: "bookcase" },
  { x: 110, y: 1142, w: 102, h: 76, kind: "piano" },
  { x: 268, y: 1320, w: 140, h: 60, kind: "table" },
  { x: 515, y: 1135, w: 174, h: 52, kind: "bookcase" },
  { x: 610, y: 1330, w: 162, h: 62, kind: "table" },
];
const wallColliders: Rect[] = [
  { x: 439, y: 70, w: 22, h: 175, kind: "wall" },
  { x: 439, y: 400, w: 22, h: 300, kind: "wall" },
  { x: 439, y: 865, w: 22, h: 325, kind: "wall" },
  { x: 439, y: 1360, w: 22, h: 170, kind: "wall" },
  { x: 50, y: 539, w: 130, h: 26, kind: "wall" },
  { x: 335, y: 539, w: 230, h: 26, kind: "wall" },
  { x: 720, y: 539, w: 130, h: 26, kind: "wall" },
  { x: 50, y: 1024, w: 130, h: 26, kind: "wall" },
  { x: 335, y: 1024, w: 230, h: 26, kind: "wall" },
  { x: 720, y: 1024, w: 130, h: 26, kind: "wall" },
];
const batterySpawnPoints: Vec[] = [
  { x: 190, y: 300 }, { x: 355, y: 255 },
  { x: 535, y: 355 }, { x: 745, y: 285 },
  { x: 175, y: 850 }, { x: 350, y: 700 },
  { x: 565, y: 840 }, { x: 745, y: 620 },
  { x: 165, y: 1420 }, { x: 350, y: 1120 },
  { x: 520, y: 1400 }, { x: 760, y: 1100 },
];

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
const distance = (a: Vec, b: Vec) => Math.hypot(a.x - b.x, a.y - b.y);
const angleDelta = (a: number, b: number) => Math.atan2(Math.sin(a - b), Math.cos(a - b));
const randomPoint = (): Vec => ({ x: 100 + Math.random() * 700, y: 125 + Math.random() * 1340 });

export function mansionGamepadMovement(gamepad: Pick<Gamepad, "axes" | "buttons"> | null | undefined): Vec {
  if (!gamepad) return { x: 0, y: 0 };
  const horizontal = Number(Boolean(gamepad.buttons[15]?.pressed)) - Number(Boolean(gamepad.buttons[14]?.pressed));
  const vertical = Number(Boolean(gamepad.buttons[13]?.pressed)) - Number(Boolean(gamepad.buttons[12]?.pressed));
  // A pressed D-pad takes priority over stick drift. Normalize diagonals to walking speed.
  const usingDpad = [12, 13, 14, 15].some((button) => gamepad.buttons[button]?.pressed);
  const x = usingDpad ? horizontal : (Math.abs(gamepad.axes[0] || 0) > .18 ? gamepad.axes[0] : 0);
  const y = usingDpad ? vertical : (Math.abs(gamepad.axes[1] || 0) > .18 ? gamepad.axes[1] : 0);
  const length = Math.max(1, Math.hypot(x, y));
  return { x: x / length, y: y / length };
}

export const chooseGhostEscapeDirection = (
  position: Vec,
  threats: readonly Vec[],
  additionalScore: (candidate: Vec) => number = () => 0,
): Vec => {
  if (!threats.length) return { x: 0, y: 0 };
  const minX = 102;
  const maxX = 798;
  const minY = 122;
  const maxY = 1478;
  const lookAhead = 150;
  const away = threats.reduce((total, threat) => ({ x: total.x + position.x - threat.x, y: total.y + position.y - threat.y }), { x: 0, y: 0 });
  const awayLength = Math.hypot(away.x, away.y);
  let bestDirection = { x: 0, y: 0 };
  let bestScore = -Infinity;

  for (let index = 0; index < 24; index++) {
    const angle = index / 24 * TAU;
    const direction = { x: Math.cos(angle), y: Math.sin(angle) };
    const candidate = {
      x: clamp(position.x + direction.x * lookAhead, minX, maxX),
      y: clamp(position.y + direction.y * lookAhead, minY, maxY),
    };
    const travel = distance(position, candidate);
    const distances = threats.map((threat) => distance(candidate, threat));
    const nearestThreat = Math.min(...distances);
    const totalDistance = distances.reduce((total, value) => total + value, 0);
    const edgeClearance = Math.min(candidate.x - minX, maxX - candidate.x, candidate.y - minY, maxY - candidate.y);
    const awayAlignment = awayLength > .01 ? (direction.x * away.x + direction.y * away.y) / awayLength : 0;
    const score = nearestThreat * 8 + totalDistance * 1.5 + travel * 4 + edgeClearance * .15 + awayAlignment * 20 + additionalScore(candidate);
    if (score > bestScore) {
      bestScore = score;
      bestDirection = direction;
    }
  }
  return bestDirection;
};

class MansionGame {
  private root: HTMLElement;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private phase: Phase = "setup";
  private role: Role = "hunter";
  private localPlayers = 1;
  private hunters: Hunter[] = [];
  private ghost = { x: 710, y: 225, energy: 100, visible: 0, attack: 0, target: 0, aiTimer: 0, escapeTimer: 0, escapeX: 0, escapeY: 0 };
  private keys = new Set<string>();
  private gamepadActions = new Set<number>();
  private pointer = { x: 0, y: 0, active: false, action: false };
  private moveTarget: Vec | null = null;
  private particles: Array<Vec & { vx: number; vy: number; life: number; color: string; size: number }> = [];
  private batteries: BatteryPickup[] = [];
  private nextBatteryId = 1;
  private batterySpawnTimer = 0;
  private motes: Array<Vec & { phase: number; speed: number }> = [];
  private animation = 0;
  private lastTime = 0;
  private remaining = 180;
  private countdown = 3;
  private countdownEndsAt = 0;
  private alertEndsAt = 0;
  private roundEndsAt = 0;
  private result = "";
  private matchId = "";
  private humanNames = ["", "", ""];
  private matchPlayers: SpectralManorResult["players"] = [];
  private onResult?: MansionVoiceOptions["onResult"];
  private shake = 0;
  private lightning = 0;
  private nextLightning = 13;
  private sound = true;
  private voicesEnabled: boolean;
  private audio: AudioContext | null = null;
  private resizeObserver: ResizeObserver;
  private onExit: () => void;
  private onVoicesEnabledChange: (enabled: boolean) => void;
  private playVoiceover: (cue: MansionVoiceCue) => void;
  private stopVoiceover: () => void;
  private background = new Image();
  private scenery: HTMLCanvasElement | null = null;
  private furnishingLayer: HTMLCanvasElement | null = null;
  private readonly keyMap = [
    { up: "KeyW", down: "KeyS", left: "KeyA", right: "KeyD", action: "Space" },
    { up: "ArrowUp", down: "ArrowDown", left: "ArrowLeft", right: "ArrowRight", action: "Enter" },
    { up: "KeyI", down: "KeyK", left: "KeyJ", right: "KeyL", action: "KeyO" },
    { up: "Numpad8", down: "Numpad5", left: "Numpad4", right: "Numpad6", action: "Numpad0" },
  ];

  constructor(root: HTMLElement, onExit: () => void, voiceOptions: MansionVoiceOptions) {
    this.root = root;
    this.humanNames[0] = voiceOptions.playerName || "";
    this.onResult = voiceOptions.onResult;
    this.onExit = onExit;
    this.voicesEnabled = voiceOptions.voicesEnabled;
    this.onVoicesEnabledChange = voiceOptions.onVoicesEnabledChange;
    this.playVoiceover = voiceOptions.playVoiceover;
    this.stopVoiceover = voiceOptions.stopVoiceover;
    this.root.innerHTML = this.shell();
    this.canvas = this.root.querySelector("canvas")!;
    this.ctx = this.canvas.getContext("2d", { alpha: false })!;
    this.background.onload = () => { this.scenery = null; };
    this.background.src = "/ghost-mansion-backdrop.png";
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.canvas.parentElement!);
    this.bind();
    this.seedMotes();
    this.resize();
    this.renderSetup();
    this.animation = requestAnimationFrame((time) => this.frame(time));
  }

  destroy() {
    this.stopVoiceover();
    cancelAnimationFrame(this.animation);
    this.resizeObserver.disconnect();
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    document.body.classList.remove("ghost-mansion-open");
  }

  private shell() {
    document.body.classList.add("ghost-mansion-open");
    return `<main class="gm-shell">
      <div class="gm-stage" tabindex="-1">
        <canvas aria-label="Spectral Manor game arena"></canvas>
        <div class="gm-vignette" aria-hidden="true"></div>
        <header class="gm-hud" hidden>
          <div class="gm-team-status"><span>HUNTERS</span><div id="gm-hunter-pips"></div></div>
          <div class="gm-clock"><small>THE VEIL CLOSES IN</small><strong id="gm-time">3:00</strong></div>
          <div class="gm-ghost-status"><span>PHANTOM</span><div><i id="gm-ghost-energy"></i></div><b id="gm-energy-label">100</b></div>
        </header>
        <aside class="gm-alert" id="gm-alert" aria-live="polite"></aside>
        <section class="gm-panel" id="gm-panel"></section>
        <div class="gm-touch" id="gm-touch" hidden>
          <div class="gm-stick" id="gm-stick"><i></i></div>
          <button class="gm-flash" id="gm-flash" type="button" aria-pressed="false"><span>✦</span><b>BEAM OFF</b></button>
        </div>
        <nav class="gm-actions">
          <button id="gm-sound" type="button" aria-label="Toggle sound">♪</button>
          <label class="gm-voices"><input id="gm-voices" type="checkbox" ${this.voicesEnabled ? "checked" : ""}><span>VOICE</span></label>
          <button id="gm-pause" type="button" aria-label="Pause game" hidden>Ⅱ</button>
          <button id="gm-exit" type="button" aria-label="Exit Spectral Manor">×</button>
        </nav>
      </div>
    </main>`;
  }

  private bind() {
    this.root.querySelector("#gm-exit")!.addEventListener("click", () => this.onExit());
    this.root.querySelector("#gm-sound")!.addEventListener("click", () => {
      this.sound = !this.sound;
      this.root.querySelector("#gm-sound")!.classList.toggle("is-muted", !this.sound);
    });
    this.root.querySelector<HTMLInputElement>("#gm-voices")!.addEventListener("change", (event) => {
      this.voicesEnabled = (event.currentTarget as HTMLInputElement).checked;
      this.onVoicesEnabledChange(this.voicesEnabled);
      if (!this.voicesEnabled) this.stopVoiceover();
    });
    this.root.querySelector("#gm-pause")!.addEventListener("click", () => this.togglePause());
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    const stage = this.root.querySelector<HTMLElement>(".gm-stage")!;
    stage.addEventListener("pointerdown", (event) => this.pointerStart(event));
    stage.addEventListener("pointermove", (event) => this.pointerMove(event));
    stage.addEventListener("pointerup", () => this.pointerEnd());
    stage.addEventListener("pointercancel", () => this.pointerEnd());
    const flashlightButton = this.root.querySelector<HTMLButtonElement>("#gm-flash")!;
    flashlightButton.addEventListener("pointerdown", (event) => {
      event.stopPropagation();
      if (this.role === "ghost") this.pointer.action = true;
    });
    flashlightButton.addEventListener("pointerup", () => {
      if (this.role === "ghost") this.pointer.action = false;
    });
    flashlightButton.addEventListener("pointercancel", () => {
      if (this.role === "ghost") this.pointer.action = false;
    });
    flashlightButton.addEventListener("click", (event) => {
      event.stopPropagation();
      if (this.role === "hunter") this.toggleHunterFlashlight(0);
    });
  }

  private onKeyDown = (event: KeyboardEvent) => {
    if (["Space", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.code)) event.preventDefault();
    this.keys.add(event.code);
    if (!event.repeat && this.role === "hunter") {
      const hunter = this.hunters.find((candidate) => candidate.human && this.keyMap[candidate.id]?.action === event.code);
      if (hunter) this.toggleHunterFlashlight(hunter.id);
    }
    if (event.code === "Escape") this.togglePause();
    if (event.code === "KeyR" && this.phase === "finished") this.start();
  };

  private onKeyUp = (event: KeyboardEvent) => this.keys.delete(event.code);

  private toggleHunterFlashlight(hunterId: number) {
    if (this.phase !== "countdown" && this.phase !== "playing") return;
    const hunter = this.hunters.find((candidate) => candidate.id === hunterId && candidate.human);
    if (!hunter) return;
    hunter.flashlightOn = !hunter.flashlightOn;
    this.syncFlashlightControl();
    this.tone(hunter.flashlightOn ? 440 : 220, .07, "square", .012);
  }

  private syncFlashlightControl() {
    const button = this.root.querySelector<HTMLButtonElement>("#gm-flash");
    if (!button) return;
    const hunter = this.role === "hunter" ? this.hunters.find((candidate) => candidate.id === 0 && candidate.human) : null;
    const flashlightOn = Boolean(hunter?.flashlightOn);
    button.classList.toggle("is-on", flashlightOn);
    button.classList.toggle("is-depleted", flashlightOn && (hunter?.battery ?? 0) <= 0);
    button.setAttribute("aria-pressed", String(flashlightOn));
    button.setAttribute("aria-label", this.role === "ghost" ? "Attack" : `Flashlight ${flashlightOn ? "on" : "off"}`);
    const label = button.querySelector("b");
    if (label) label.textContent = this.role === "ghost" ? "STRIKE" : flashlightOn ? "BEAM ON" : "BEAM OFF";
  }

  private renderSetup() {
    const panel = this.root.querySelector<HTMLElement>("#gm-panel")!;
    const rosterCopy = this.localPlayers === 1
      ? "One local player · two computer companions"
      : this.localPlayers === 2
        ? "Two local players · one computer companion"
        : "Three local players · no computer companions";
    panel.className = "gm-panel gm-panel--setup";
    panel.innerHTML = `<div class="gm-title-lockup">
      <p>PARTY STACK PRESENTS</p>
      <h1><span>SPECTRAL</span><strong>MANOR</strong></h1>
      <div class="gm-title-ghost" aria-hidden="true"><i></i><b>✦</b><em>✦</em></div>
      <p class="gm-subtitle">A LIGHT-IN-THE-DARK PARTY HUNT</p>
    </div>
    <div class="gm-setup-card">
      <div class="gm-role-picker">
        <button class="${this.role === "hunter" ? "is-active" : ""}" data-role="hunter" type="button"><i>⌁</i><span><small>RECOMMENDED</small><strong>Hunt the phantom</strong><em>One-screen play with a cunning ghost controlled by the manor.</em></span></button>
        <button class="${this.role === "ghost" ? "is-active" : ""}" data-role="ghost" type="button"><i>◉</i><span><small>MASTER THE MANOR</small><strong>Become the phantom</strong><em>Outwit three computer-controlled lantern keepers.</em></span></button>
      </div>
      <div class="gm-player-picker ${this.role === "ghost" ? "is-disabled" : ""}">
        <span>LOCAL HUNTERS</span>
        <div>${[1,2,3].map((count) => `<button class="${this.localPlayers === count ? "is-active" : ""}" data-players="${count}" type="button">${count}</button>`).join("")}</div>
        <small>${rosterCopy}</small>
      </div>
      <div class="gm-names">${this.humanNames.slice(0, this.role === "ghost" ? 1 : this.localPlayers).map((name, index) => `<label>Player ${index + 1} name<input data-manor-name="${index}" maxlength="24" autocomplete="nickname" value="${name.replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char]!)}" placeholder="Use your usual leaderboard name"></label>`).join("")}<small>Named results are added to the leaderboard. Computer opponents have (AI) in their names.</small><span id="gm-name-error" role="alert"></span></div>
      <button class="gm-start" id="gm-start" type="button"><span>ENTER THE MANOR</span><b>→</b></button>
      <div class="gm-setup-notes"><span><b>MOVE</b> Stick / D-pad · WASD / arrows / click</span><span><b>TOGGLE BEAM</b> Space / A / tap</span><span><b>BATTERIES</b> Two pickups rematerialize</span><span><b>ONE SCREEN</b> No room or shared display required</span></div>
    </div>`;
    panel.querySelectorAll<HTMLButtonElement>("[data-role]").forEach((button) => button.addEventListener("click", () => {
      this.role = button.dataset.role as Role;
      this.renderSetup();
    }));
    panel.querySelectorAll<HTMLButtonElement>("[data-players]").forEach((button) => button.addEventListener("click", () => {
      this.localPlayers = Number(button.dataset.players);
      this.renderSetup();
    }));
    panel.querySelector("#gm-start")!.addEventListener("click", () => this.start());
    panel.querySelectorAll<HTMLInputElement>("[data-manor-name]").forEach((input) => input.addEventListener("input", () => {
      this.humanNames[Number(input.dataset.manorName)] = input.value;
    }));
  }

  private start() {
    const count = this.role === "ghost" ? 1 : this.localPlayers;
    if (this.humanNames.slice(0, count).some((name) => !name.trim())) {
      const error = this.root.querySelector("#gm-name-error");
      if (error) error.textContent = "Enter a name for each player before starting.";
      return;
    }
    this.matchPlayers = spectralManorPlayers(this.role, this.localPlayers, this.humanNames);
    if (new Set(this.matchPlayers.map((player) => player.name.normalize("NFKC").toLowerCase().trim().replace(/\s+/g, " "))).size !== 4) {
      const error = this.root.querySelector("#gm-name-error");
      if (error) error.textContent = "Use a different name for each player.";
      return;
    }
    this.matchId = crypto.randomUUID();
    this.unlockAudio();
    this.stopVoiceover();
    this.hunters = [
      { x: 145, y: 1400, facing: -Math.PI / 2, health: 100, battery: 100, id: 0 },
      { x: 700, y: 900, facing: Math.PI, health: 100, battery: 100, id: 1 },
      { x: 145, y: 420, facing: 0, health: 100, battery: 100, id: 2 },
    ].map((hunter) => ({ ...hunter, color: hunterColors[hunter.id], human: this.role === "hunter" && hunter.id < this.localPlayers, down: false, revive: 0, pulse: 0, flashlightOn: false, aiTarget: randomPoint(), aiTimer: Math.random() * 2, ghostLastSeen: null, ghostMemory: 0 }));
    this.ghost = { x: 710, y: 225, energy: 100, visible: 0, attack: 0, target: 0, aiTimer: 0, escapeTimer: 0, escapeX: 0, escapeY: 0 };
    this.remaining = 180;
    this.countdown = 3.4;
    this.countdownEndsAt = performance.now() + 3400;
    this.roundEndsAt = 0;
    this.lastTime = performance.now();
    this.result = "";
    this.keys.clear();
    this.gamepadActions.clear();
    this.pointer.action = false;
    this.moveTarget = null;
    this.particles = [];
    this.batteries = [];
    this.nextBatteryId = 1;
    this.batterySpawnTimer = 7;
    this.spawnBattery();
    this.spawnBattery();
    this.phase = "countdown";
    const panel = this.root.querySelector<HTMLElement>("#gm-panel")!;
    panel.className = "gm-panel gm-panel--hidden";
    panel.innerHTML = "";
    this.root.querySelector<HTMLElement>(".gm-hud")!.hidden = false;
    this.root.querySelector<HTMLElement>("#gm-pause")!.hidden = false;
    this.root.querySelector<HTMLElement>("#gm-touch")!.hidden = !this.usesTouchControls();
    this.syncFlashlightControl();
    this.root.querySelector<HTMLElement>(".gm-stage")!.focus({ preventScroll: true });
    this.showAlert("LIGHTS OUT", "Find what hides between the rooms");
    if (this.voicesEnabled) this.playVoiceover("spectral-manor:lights-out");
    this.tone(196, .4, "sine");
  }

  private togglePause() {
    if (this.phase === "playing") {
      this.phase = "paused";
      const panel = this.root.querySelector<HTMLElement>("#gm-panel")!;
      panel.className = "gm-panel gm-panel--pause";
      panel.innerHTML = `<p>THE MANOR IS WAITING</p><h2>Paused</h2><button id="gm-resume" type="button">RETURN TO THE HUNT</button><button id="gm-restart" type="button">RESTART ROUND</button>`;
      panel.querySelector("#gm-resume")!.addEventListener("click", () => this.togglePause());
      panel.querySelector("#gm-restart")!.addEventListener("click", () => this.start());
    } else if (this.phase === "paused") {
      this.phase = "playing";
      this.root.querySelector<HTMLElement>("#gm-panel")!.className = "gm-panel gm-panel--hidden";
      this.lastTime = performance.now();
      this.roundEndsAt = this.lastTime + this.remaining * 1000;
      this.root.querySelector<HTMLElement>(".gm-stage")!.focus({ preventScroll: true });
    }
  }

  private finish(huntersWin: boolean) {
    if (this.phase === "finished") return;
    this.phase = "finished";
    this.result = huntersWin ? "THE LIGHT PREVAILS" : "THE MANOR CLAIMS THE NIGHT";
    this.root.querySelector<HTMLElement>("#gm-pause")!.hidden = true;
    this.root.querySelector<HTMLElement>("#gm-touch")!.hidden = true;
    const panel = this.root.querySelector<HTMLElement>("#gm-panel")!;
    panel.className = `gm-panel gm-panel--result ${huntersWin ? "is-hunter-win" : "is-ghost-win"}`;
    panel.innerHTML = `<div class="gm-result-mark">${huntersWin ? "✦" : "◉"}</div><p>ROUND COMPLETE</p><h2>${this.result}</h2><span>${huntersWin ? "The keepers burned through the phantom’s veil." : "The final lantern faded before the bells rang."}</span><div><button id="gm-again" type="button">Rematch</button><button id="gm-menu" type="button">CHANGE SIDES</button></div><small>Press R to rematch</small>`;
    panel.querySelector("#gm-again")!.addEventListener("click", () => this.start());
    panel.querySelector("#gm-menu")!.addEventListener("click", () => { this.phase = "setup"; this.root.querySelector<HTMLElement>(".gm-hud")!.hidden = true; this.renderSetup(); });
    if (this.onResult) {
      const status = document.createElement("small"); status.className = "gm-record-status"; status.setAttribute("role", "status");
      status.textContent = "Saving the leaderboard result…"; panel.append(status);
      this.onResult({ id: this.matchId, players: this.matchPlayers, winnerTeam: huntersWin ? "hunters" : "phantom" })
        .then((saved) => { status.textContent = saved ? "Result recorded on the all-time leaderboard." : "Result saved in this browser. It will sync when the connection returns."; })
        .catch(() => { status.textContent = "The leaderboard result could not be saved in this browser."; });
    }
    this.burst(huntersWin ? this.ghost : { x: 800, y: 430 }, huntersWin ? "#75fff0" : "#b578ff", 80);
    if (this.voicesEnabled) this.playVoiceover(huntersWin ? "spectral-manor:hunters-win" : "spectral-manor:phantom-win");
    [262, 330, 392, 523].forEach((frequency, index) => window.setTimeout(() => this.tone(frequency, .28, "triangle"), index * 120));
  }

  private frame(time: number) {
    if (this.alertEndsAt > 0 && time >= this.alertEndsAt) {
      this.root.querySelector("#gm-alert")?.classList.remove("is-showing");
      this.alertEndsAt = 0;
    }
    const elapsed = Math.min(.25, Math.max(0, (time - (this.lastTime || time)) / 1000));
    this.lastTime = time;
    if (this.phase === "countdown") {
      this.countdown = Math.max(0, (this.countdownEndsAt - time) / 1000);
      if (this.countdown <= 0) {
        this.phase = "playing";
        this.roundEndsAt = this.countdownEndsAt + this.remaining * 1000;
        this.showAlert("HUNT", "Toggle your beam · collect glowing batteries");
        if (this.voicesEnabled) this.playVoiceover("spectral-manor:hunt-begins");
      }
    } else if (this.phase === "playing") {
      this.remaining = Math.max(0, (this.roundEndsAt - time) / 1000);
      let simulationRemaining = elapsed;
      while (simulationRemaining > 0 && this.phase === "playing") {
        const step = Math.min(.035, simulationRemaining);
        this.update(step);
        simulationRemaining -= step;
      }
    }
    this.draw(time / 1000);
    this.updateHud();
    this.animation = requestAnimationFrame((next) => this.frame(next));
  }

  private update(dt: number) {
    this.nextLightning -= dt;
    if (this.nextLightning <= 0) {
      this.lightning = .32;
      this.nextLightning = 11 + Math.random() * 15;
      this.tone(58, .55, "sawtooth", .035);
    }
    this.lightning = Math.max(0, this.lightning - dt);
    this.ghost.visible = Math.max(0, this.ghost.visible - dt);
    this.ghost.attack = Math.max(0, this.ghost.attack - dt);
    this.shake = Math.max(0, this.shake - dt * 20);

    this.hunters.forEach((hunter) => this.updateHunter(hunter, dt));
    this.updateBatteries(dt);
    this.updateGhost(dt);
    this.updateRevives(dt);
    this.particles.forEach((particle) => {
      particle.x += particle.vx * dt;
      particle.y += particle.vy * dt;
      particle.life -= dt;
      particle.vx *= .985;
      particle.vy *= .985;
    });
    this.particles = this.particles.filter((particle) => particle.life > 0);
    if (this.ghost.energy <= 0) this.finish(true);
    else if (this.remaining <= 0 || this.hunters.every((hunter) => hunter.down)) this.finish(false);
  }

  private updateHunter(hunter: Hunter, dt: number) {
    if (hunter.down) return;
    let input = { x: 0, y: 0, action: false };
    if (hunter.human) {
      const map = this.keyMap[hunter.id];
      input.x = Number(this.keys.has(map.right)) - Number(this.keys.has(map.left));
      input.y = Number(this.keys.has(map.down)) - Number(this.keys.has(map.up));
      input.action = hunter.flashlightOn;
      if (hunter.id === 0 && this.localPlayers === 1) {
        input.x += Number(this.keys.has("ArrowRight")) - Number(this.keys.has("ArrowLeft"));
        input.y += Number(this.keys.has("ArrowDown")) - Number(this.keys.has("ArrowUp"));
      }
      const gamepad = navigator.getGamepads?.()[hunter.id];
      if (gamepad) {
        const movement = mansionGamepadMovement(gamepad);
        input.x += movement.x;
        input.y += movement.y;
        const actionPressed = Boolean(gamepad.buttons[0]?.pressed || gamepad.buttons[7]?.pressed);
        if (actionPressed && !this.gamepadActions.has(hunter.id)) this.toggleHunterFlashlight(hunter.id);
        if (actionPressed) this.gamepadActions.add(hunter.id);
        else this.gamepadActions.delete(hunter.id);
        input.action = hunter.flashlightOn;
      } else {
        this.gamepadActions.delete(hunter.id);
      }
      if (hunter.id === 0 && this.pointer.active) {
        input.x += this.pointer.x;
        input.y += this.pointer.y;
      }
      if (hunter.id === 0) {
        const hasManualMovement = Math.hypot(input.x, input.y) > .05;
        if (hasManualMovement) this.moveTarget = null;
        else if (this.moveTarget) {
          const dx = this.moveTarget.x - hunter.x;
          const dy = this.moveTarget.y - hunter.y;
          const targetDistance = Math.hypot(dx, dy);
          if (targetDistance <= 8) this.moveTarget = null;
          else {
            input.x = dx / targetDistance;
            input.y = dy / targetDistance;
          }
        }
      }
    } else {
      input = this.hunterAi(hunter, dt);
    }
    const length = Math.hypot(input.x, input.y);
    if (length > .05) {
      input.x /= Math.max(1, length);
      input.y /= Math.max(1, length);
      hunter.facing = Math.atan2(input.y, input.x);
      this.moveCircle(hunter, input.x * 148 * dt, input.y * 148 * dt, 24);
    }
    hunter.pulse = input.action && hunter.battery > 0 ? 1 : Math.max(0, hunter.pulse - dt * 3.2);
    hunter.battery = clamp(hunter.battery + (input.action ? -FLASHLIGHT_DRAIN : FLASHLIGHT_RECOVERY) * dt, 0, 100);
    const beamLength = hunter.pulse ? FOCUSED_BEAM_LENGTH : GUIDE_BEAM_LENGTH;
    const toGhost = Math.atan2(this.ghost.y - hunter.y, this.ghost.x - hunter.x);
    const caught = distance(hunter, this.ghost) < beamLength && Math.abs(angleDelta(toGhost, hunter.facing)) < (hunter.pulse ? FOCUSED_BEAM_SPREAD : GUIDE_BEAM_SPREAD) && this.hasLineOfSight(hunter, this.ghost);
    if (caught) {
      const drain = (hunter.pulse ? 16 : 4) * dt;
      this.ghost.energy = Math.max(0, this.ghost.energy - drain);
      this.ghost.visible = .32;
      if (!hunter.human) {
        hunter.ghostLastSeen = { x: this.ghost.x, y: this.ghost.y };
        hunter.ghostMemory = .8;
      }
      if (Math.random() < dt * 22) this.spark(this.ghost.x, this.ghost.y, hunter.color);
    }
  }

  private hunterAi(hunter: Hunter, dt: number) {
    hunter.aiTimer -= dt;
    hunter.ghostMemory = Math.max(0, hunter.ghostMemory - dt);
    const seesGhost = this.hunterCanSeeGhost(hunter);
    if (seesGhost) {
      hunter.ghostLastSeen = { x: this.ghost.x, y: this.ghost.y };
      hunter.ghostMemory = .8;
    }
    const nearestBattery = hunter.battery < 24
      ? this.batteries.reduce<BatteryPickup | null>((nearest, battery) => !nearest || distance(hunter, battery) < distance(hunter, nearest) ? battery : nearest, null)
      : null;
    if (seesGhost) hunter.aiTarget = { x: this.ghost.x, y: this.ghost.y };
    else if (hunter.ghostMemory > 0 && hunter.ghostLastSeen) hunter.aiTarget = { ...hunter.ghostLastSeen };
    else if (nearestBattery) hunter.aiTarget = { x: nearestBattery.x, y: nearestBattery.y };
    else if (hunter.aiTimer <= 0 || distance(hunter, hunter.aiTarget) < 45) {
      hunter.aiTarget = randomPoint();
      hunter.aiTimer = 2.4 + Math.random() * 3.8;
    }
    const navigationTarget = this.aiNavigationTarget(hunter, hunter.aiTarget);
    const angle = Math.atan2(navigationTarget.y - hunter.y, navigationTarget.x - hunter.x);
    const investigatingReveal = hunter.ghostMemory > 0
      && hunter.ghostLastSeen !== null
      && distance(hunter, hunter.ghostLastSeen) < FOCUSED_BEAM_LENGTH + 24;
    return { x: Math.cos(angle), y: Math.sin(angle), action: (seesGhost || investigatingReveal) && hunter.battery > 4 };
  }

  private hunterCanSeeGhost(hunter: Hunter) {
    return this.hunterBeamHitsPoint(hunter, this.ghost);
  }

  private hunterBeamHitsPoint(hunter: Hunter, point: Vec) {
    const beamLength = hunter.pulse ? FOCUSED_BEAM_LENGTH : GUIDE_BEAM_LENGTH;
    const beamSpread = hunter.pulse ? FOCUSED_BEAM_SPREAD : GUIDE_BEAM_SPREAD;
    const toGhost = Math.atan2(point.y - hunter.y, point.x - hunter.x);
    return distance(hunter, point) < beamLength
      && Math.abs(angleDelta(toGhost, hunter.facing)) < beamSpread
      && this.hasLineOfSight(hunter, point);
  }

  private aiNavigationTarget(hunter: Hunter, destination: Vec) {
    const roomFor = (point: Vec) => ({
      column: point.x < 450 ? 0 : 1,
      row: point.y < 552 ? 0 : point.y < 1037 ? 1 : 2,
    });
    const currentRoom = roomFor(hunter);
    const destinationRoom = roomFor(destination);
    if (currentRoom.row !== destinationRoom.row) {
      const movingDown = destinationRoom.row > currentRoom.row;
      const boundaryY = movingDown
        ? (currentRoom.row === 0 ? 552 : 1037)
        : (currentRoom.row === 2 ? 1037 : 552);
      return { x: currentRoom.column === 0 ? 257 : 642, y: boundaryY };
    }
    if (currentRoom.column !== destinationRoom.column) {
      return { x: 450, y: [322, 782, 1275][currentRoom.row] };
    }
    return destination;
  }

  private updateBatteries(dt: number) {
    this.batterySpawnTimer -= dt;
    for (const battery of [...this.batteries]) {
      const collector = this.hunters.find((hunter) => !hunter.down && hunter.battery < 96 && distance(hunter, battery) < 42);
      if (!collector) continue;
      collector.battery = Math.min(100, collector.battery + BATTERY_RESTORE);
      this.batteries = this.batteries.filter((candidate) => candidate.id !== battery.id);
      this.burst(battery, "#ffe67c", 24);
      this.tone(659, .18, "triangle", .025);
      if (collector.human) this.showAlert("BATTERY FOUND", "Your focused beam is recharged");
      this.batterySpawnTimer = Math.min(this.batterySpawnTimer, 6);
    }
    if (this.batteries.length < MAX_BATTERY_PICKUPS && this.batterySpawnTimer <= 0) {
      this.spawnBattery();
      this.batterySpawnTimer = 6 + Math.random() * 3;
    }
  }

  private spawnBattery() {
    const candidates = batterySpawnPoints.filter((point) =>
      this.batteries.every((battery) => distance(point, battery) > 180)
      && this.hunters.every((hunter) => distance(point, hunter) > 80)
      && distance(point, this.ghost) > 90
    );
    const point = candidates[Math.floor(Math.random() * candidates.length)] || batterySpawnPoints[Math.floor(Math.random() * batterySpawnPoints.length)];
    const battery = { ...point, id: this.nextBatteryId++, phase: Math.random() * TAU };
    this.batteries.push(battery);
    this.burst(battery, "#ffe67c", 12);
  }

  private updateGhost(dt: number) {
    let dx = 0;
    let dy = 0;
    if (this.role === "ghost") {
      const map = this.keyMap[0];
      dx = Number(this.keys.has(map.right) || this.keys.has("ArrowRight")) - Number(this.keys.has(map.left) || this.keys.has("ArrowLeft"));
      dy = Number(this.keys.has(map.down) || this.keys.has("ArrowDown")) - Number(this.keys.has(map.up) || this.keys.has("ArrowUp"));
      const gamepad = navigator.getGamepads?.()[0];
      const movement = mansionGamepadMovement(gamepad);
      dx += movement.x; dy += movement.y;
      if (this.pointer.active) { dx += this.pointer.x; dy += this.pointer.y; }
      if (Math.hypot(dx, dy) > .05) this.moveTarget = null;
      else if (this.moveTarget) {
        const targetDx = this.moveTarget.x - this.ghost.x;
        const targetDy = this.moveTarget.y - this.ghost.y;
        const targetDistance = Math.hypot(targetDx, targetDy);
        if (targetDistance <= 8) this.moveTarget = null;
        else { dx = targetDx / targetDistance; dy = targetDy / targetDistance; }
      }
    } else {
      const active = this.hunters.filter((hunter) => !hunter.down);
      const litBy = active.filter((hunter) => this.hunterBeamHitsPoint(hunter, this.ghost));
      this.ghost.aiTimer -= dt;
      this.ghost.escapeTimer = Math.max(0, this.ghost.escapeTimer - dt);
      if (litBy.length) {
        const escape = chooseGhostEscapeDirection(this.ghost, litBy, (candidate) =>
          -active.filter((hunter) => this.hunterBeamHitsPoint(hunter, candidate)).length * 2500
        );
        dx = escape.x;
        dy = escape.y;
        this.ghost.escapeX = escape.x;
        this.ghost.escapeY = escape.y;
        this.ghost.escapeTimer = 1.15;
      } else if (this.ghost.escapeTimer > 0) {
        dx = this.ghost.escapeX;
        dy = this.ghost.escapeY;
      } else {
        if (this.ghost.aiTimer <= 0 || !active[this.ghost.target] || active[this.ghost.target].down) {
          this.ghost.target = Math.floor(Math.random() * Math.max(1, active.length));
          this.ghost.aiTimer = 2.2 + Math.random() * 2.6;
        }
        const target = active[this.ghost.target] || active[0];
        if (target) { dx = target.x - this.ghost.x; dy = target.y - this.ghost.y; }
      }
    }
    const length = Math.hypot(dx, dy);
    const speed = this.role !== "ghost" && this.ghost.escapeTimer > 0 ? 248 : 176;
    if (length > .1) this.moveCircle(this.ghost, dx / length * speed * dt, dy / length * speed * dt, 28, true);
    const victim = this.hunters.filter((hunter) => !hunter.down).sort((a, b) => distance(a, this.ghost) - distance(b, this.ghost))[0];
    const wantsAttack = this.role === "ghost" ? (this.keys.has("Space") || this.pointer.action || Boolean(navigator.getGamepads?.()[0]?.buttons[0]?.pressed)) : true;
    if (victim && wantsAttack && distance(victim, this.ghost) < 62 && this.ghost.attack <= 0) {
      victim.health = Math.max(0, victim.health - 34);
      this.ghost.attack = 1.2;
      this.ghost.visible = .18;
      this.alertAiHuntersToAttack();
      this.shake = 12;
      this.burst(victim, "#b780ff", 22);
      this.tone(90, .25, "sawtooth", .04);
      navigator.vibrate?.([55, 35, 75]);
      if (victim.health <= 0) {
        victim.down = true;
        this.showAlert("LANTERN LOST", `Keeper ${victim.id + 1} needs help`);
        if (this.voicesEnabled) this.playVoiceover("spectral-manor:lantern-lost");
      }
    }
  }

  private alertAiHuntersToAttack() {
    const attackPosition = { x: this.ghost.x, y: this.ghost.y };
    this.hunters.filter((hunter) => !hunter.human && !hunter.down).forEach((hunter) => {
      hunter.ghostLastSeen = { ...attackPosition };
      hunter.ghostMemory = 9;
      hunter.aiTarget = { ...attackPosition };
      hunter.aiTimer = 9;
      hunter.facing = Math.atan2(attackPosition.y - hunter.y, attackPosition.x - hunter.x);
      if (hunter.battery > 4) hunter.pulse = 1;
    });
  }

  private updateRevives(dt: number) {
    this.hunters.filter((hunter) => hunter.down).forEach((downed) => {
      const helper = this.hunters.find((hunter) => !hunter.down && distance(hunter, downed) < 74);
      downed.revive = helper ? downed.revive + dt : Math.max(0, downed.revive - dt * .5);
      if (downed.revive >= 2.2) {
        downed.down = false;
        downed.health = 55;
        downed.revive = 0;
        this.burst(downed, downed.color, 24);
        this.showAlert("LANTERN RELIT", `Keeper ${downed.id + 1} is back in the hunt`);
        if (this.voicesEnabled) this.playVoiceover("spectral-manor:lantern-relit");
      }
    });
  }

  private moveCircle(entity: Vec, dx: number, dy: number, radius: number, ghost = false) {
    const tryMove = (axis: "x" | "y", amount: number) => {
      entity[axis] += amount;
      entity.x = clamp(entity.x, 74 + radius, 826 - radius);
      entity.y = clamp(entity.y, 94 + radius, 1506 - radius);
      if (!ghost) [...furniture, ...wallColliders].forEach((rect) => {
        const closestX = clamp(entity.x, rect.x, rect.x + rect.w);
        const closestY = clamp(entity.y, rect.y, rect.y + rect.h);
        const dist = Math.hypot(entity.x - closestX, entity.y - closestY);
        if (dist < radius) entity[axis] -= amount;
      });
    };
    tryMove("x", dx);
    tryMove("y", dy);
  }

  private hasLineOfSight(start: Vec, end: Vec) {
    const blockers = [...wallColliders, ...furniture];
    const steps = Math.ceil(distance(start, end) / 12);
    for (let step = 2; step < steps - 1; step++) {
      const amount = step / steps;
      const x = start.x + (end.x - start.x) * amount;
      const y = start.y + (end.y - start.y) * amount;
      if (blockers.some((rect) => x >= rect.x && x <= rect.x + rect.w && y >= rect.y && y <= rect.y + rect.h)) return false;
    }
    return true;
  }

  private draw(time: number) {
    const ctx = this.ctx;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = "#02040b";
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    const scale = Math.min(this.canvas.width / W, this.canvas.height / H);
    const offsetX = (this.canvas.width - W * scale) / 2;
    const offsetY = (this.canvas.height - H * scale) / 2;
    ctx.setTransform(scale, 0, 0, scale, offsetX, offsetY);
    ctx.save();
    if (this.shake) ctx.translate((Math.random() - .5) * this.shake, (Math.random() - .5) * this.shake);
    if (!this.scenery) this.scenery = this.cacheScenery(() => this.drawMansion());
    if (!this.furnishingLayer) this.furnishingLayer = this.cacheScenery(() => this.drawFurniture());
    ctx.drawImage(this.scenery, 0, 0);
    this.drawLightPools(time);
    ctx.drawImage(this.furnishingLayer, 0, 0);
    this.drawAtmosphere(time);
    paintSconceFlames(ctx, time);
    this.drawBatteries(time);
    this.drawMoveTarget(time);
    this.hunters.forEach((hunter) => this.drawHunter(hunter, time));
    this.drawGhost(time);
    this.drawParticles();
    if (this.phase === "countdown") this.drawCountdown();
    if (this.lightning > 0) {
      ctx.fillStyle = `rgba(200,225,255,${this.lightning * 1.65})`;
      ctx.fillRect(0, 0, W, H);
    }
    ctx.restore();
  }

  private cacheScenery(draw: () => void) {
    const layer = document.createElement("canvas");
    layer.width = W; layer.height = H;
    const original = this.ctx;
    this.ctx = layer.getContext("2d")!;
    try { draw(); } finally { this.ctx = original; }
    return layer;
  }

  private drawMansion() {
    const ctx = this.ctx;
    const bg = ctx.createRadialGradient(450, 800, 90, 450, 800, 900);
    bg.addColorStop(0, "#172338");
    bg.addColorStop(.62, "#080d1c");
    bg.addColorStop(1, "#02040b");
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);
    if (this.background.complete && this.background.naturalWidth) {
      ctx.save();
      ctx.globalAlpha = .16;
      ctx.filter = "saturate(.75) contrast(1.25) brightness(.55)";
      ctx.drawImage(this.background, 0, 0, W, H);
      ctx.restore();
    }
    ctx.fillStyle = "#070c17";
    ctx.shadowColor = "#000";
    ctx.shadowBlur = 55;
    ctx.fillRect(50, 70, 800, 1460);
    ctx.shadowBlur = 0;
    const floors = [
      { x: 74, y: 94, w: 365, h: 445, color: "#173237" },
      { x: 461, y: 94, w: 365, h: 445, color: "#292235" },
      { x: 74, y: 565, w: 365, h: 459, color: "#24303b" },
      { x: 461, y: 565, w: 365, h: 459, color: "#192d3d" },
      { x: 74, y: 1050, w: 365, h: 456, color: "#3a1e2a" },
      { x: 461, y: 1050, w: 365, h: 456, color: "#352615" },
    ];
    floors.forEach((floor, index) => {
      const grad = ctx.createLinearGradient(floor.x, floor.y, floor.x + floor.w, floor.y + floor.h);
      grad.addColorStop(0, floor.color);
      grad.addColorStop(1, "#0b1320");
      ctx.fillStyle = grad;
      ctx.fillRect(floor.x, floor.y, floor.w, floor.h);
      ctx.strokeStyle = "rgba(223,188,107,.07)";
      ctx.lineWidth = 1;
      const spacing = index === 4 ? 34 : 42;
      for (let x = floor.x; x < floor.x + floor.w; x += spacing) {
        ctx.beginPath(); ctx.moveTo(x, floor.y); ctx.lineTo(x, floor.y + floor.h); ctx.stroke();
      }
      for (let y = floor.y; y < floor.y + floor.h; y += spacing) {
        ctx.beginPath(); ctx.moveTo(floor.x, y); ctx.lineTo(floor.x + floor.w, y); ctx.stroke();
      }
      paintManorRoom(ctx, floor, index);
      ctx.fillStyle = "rgba(235,211,165,.5)";
      ctx.font = "700 18px system-ui";
      ctx.textAlign = "center";
      ctx.font = "700 13px system-ui";
      ctx.letterSpacing = "1.5px";
      ctx.fillText(roomNames[Math.floor(index / 2)][index % 2], floor.x + floor.w / 2, floor.y + floor.h - 22);
      ctx.letterSpacing = "0px";
    });
    this.drawWalls();
  }

  private drawAtmosphere(time: number) {
    const ctx = this.ctx;
    ctx.save();
    this.motes.forEach((mote) => {
      const alpha = .08 + Math.sin(time * mote.speed + mote.phase) * .05;
      ctx.fillStyle = `rgba(159,218,224,${alpha})`;
      ctx.beginPath(); ctx.arc(mote.x, mote.y + Math.sin(time + mote.phase) * 7, 1.5, 0, TAU); ctx.fill();
    });
    ctx.restore();
  }


  private drawWalls() {
    const ctx = this.ctx;
    const wall = (x: number, y: number, w: number, h: number) => {
      const grad = ctx.createLinearGradient(x, y, x, y + h);
      grad.addColorStop(0, "#ccb584");
      grad.addColorStop(.12, "#857359");
      grad.addColorStop(.3, "#4c4b51");
      grad.addColorStop(1, "#18202d");
      ctx.fillStyle = grad;
      ctx.shadowColor = "rgba(0,0,0,.75)";
      ctx.shadowBlur = 10;
      ctx.shadowOffsetY = 7;
      ctx.fillRect(x, y, w, h);
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;
      ctx.fillStyle = "rgba(255,220,145,.25)";
      ctx.fillRect(x, y, w, 3);
      ctx.strokeStyle = "#e4d0a04d"; ctx.lineWidth = 1;
      ctx.strokeRect(x + 3, y + 3, w - 6, h - 6);
      ctx.fillStyle = "#070d1c55";
      if (w > h) {
        for (let joint = x + 34; joint < x + w - 8; joint += 42) ctx.fillRect(joint, y + 5, 2, h - 7);
      } else {
        for (let joint = y + 34; joint < y + h - 8; joint += 42) ctx.fillRect(x + 4, joint, w - 6, 2);
      }
    };
    wall(50, 70, 800, 24); wall(50, 1506, 800, 24); wall(50, 70, 24, 1460); wall(826, 70, 24, 1460);
    wallColliders.forEach((rect) => wall(rect.x, rect.y, rect.w, rect.h));
    [539,1024].forEach((y) => [180,335,565,720].forEach((x) => {
      ctx.fillStyle = "#c4a567"; ctx.fillRect(x - 6, y - 8, 12, 42);
    }));
    [245,400,700,865,1190,1360].forEach((y) => {
      ctx.fillStyle = "#c4a567"; ctx.fillRect(431, y - 6, 38, 12);
    });
  }

  private drawLightPools(time: number) {
    const ctx = this.ctx;
    ctx.save();
    ctx.globalCompositeOperation = "screen";
    [[215,235,"75,160,173"],[675,240,"130,117,205"],[220,760,"85,138,169"],[670,770,"74,128,171"],[220,1250,"190,74,89"],[670,1260,"175,128,58"]].forEach(([x,y,c], index) => {
      const pulse = 145 + Math.sin(time * 1.3 + index) * 10;
      const gradient = ctx.createRadialGradient(Number(x), Number(y), 0, Number(x), Number(y), pulse);
      gradient.addColorStop(0, `rgba(${c},.16)`); gradient.addColorStop(1, `rgba(${c},0)`);
      ctx.fillStyle = gradient; ctx.beginPath(); ctx.arc(Number(x), Number(y), pulse, 0, TAU); ctx.fill();
    });
    this.hunters.filter((hunter) => !hunter.down).forEach((hunter) => this.drawBeam(hunter));
    ctx.restore();
  }

  private drawBeam(hunter: Hunter) {
    const ctx = this.ctx;
    const length = hunter.pulse ? FOCUSED_BEAM_LENGTH : GUIDE_BEAM_LENGTH;
    const spread = hunter.pulse ? FOCUSED_BEAM_SPREAD : GUIDE_BEAM_SPREAD;
    const gradient = ctx.createRadialGradient(hunter.x, hunter.y, 5, hunter.x, hunter.y, length);
    gradient.addColorStop(0, `${hunter.color}aa`);
    gradient.addColorStop(.5, `${hunter.color}42`);
    gradient.addColorStop(1, `${hunter.color}00`);
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.moveTo(hunter.x, hunter.y);
    // Clip the illustration to the same solid scenery that blocks flashlight hits.
    for (let ray = 0; ray <= 20; ray++) {
      const angle = hunter.facing - spread + spread * 2 * ray / 20;
      const dx = Math.cos(angle); const dy = Math.sin(angle);
      let reach = length;
      for (const rect of [...wallColliders, ...furniture]) {
        let near = 0; let far = reach;
        for (const [origin, direction, low, high] of [[hunter.x, dx, rect.x, rect.x + rect.w], [hunter.y, dy, rect.y, rect.y + rect.h]]) {
          if (Math.abs(direction) < .00001) { if (origin < low || origin > high) far = -1; }
          else {
            const a = (low - origin) / direction; const b = (high - origin) / direction;
            near = Math.max(near, Math.min(a, b)); far = Math.min(far, Math.max(a, b));
          }
        }
        if (far >= near && near > 0) reach = Math.min(reach, near);
      }
      ctx.lineTo(hunter.x + dx * reach, hunter.y + dy * reach);
    }
    ctx.closePath(); ctx.fill();
    if (hunter.pulse) {
      ctx.save(); ctx.clip();
      const core = ctx.createRadialGradient(hunter.x, hunter.y, 0, hunter.x, hunter.y, length);
      core.addColorStop(0, "#fffbd799"); core.addColorStop(.35, "#fffbd72b"); core.addColorStop(1, "#fffbd700");
      ctx.fillStyle = core; ctx.beginPath(); ctx.moveTo(hunter.x, hunter.y);
      ctx.arc(hunter.x, hunter.y, length, hunter.facing - spread * .45, hunter.facing + spread * .45); ctx.fill(); ctx.restore();
    }
  }

  private drawFurniture() {
    furniture.forEach((item) => {
      const ctx = this.ctx;
      ctx.save();
      ctx.shadowColor = "rgba(0,0,0,.65)"; ctx.shadowBlur = 12; ctx.shadowOffsetY = 8;
      const grad = ctx.createLinearGradient(item.x, item.y, item.x, item.y + item.h);
      grad.addColorStop(0, item.kind === "sofa" ? "#744355" : "#80633d"); grad.addColorStop(1, "#251c20");
      ctx.fillStyle = grad; ctx.strokeStyle = "#c2a068"; ctx.lineWidth = 2;
      this.roundRect(item.x, item.y, item.w, item.h, item.kind === "table" ? 25 : 9); ctx.fill(); ctx.stroke();
      ctx.shadowBlur = 0; ctx.shadowOffsetY = 0;
      if (item.kind === "bookcase") {
        for (let x = item.x + 12; x < item.x + item.w - 8; x += 16) {
          ctx.fillStyle = ["#9d4656", "#405f78", "#9d7b3d"][Math.floor(x / 16) % 3];
          ctx.fillRect(x, item.y + 12, 9, item.h - 24);
          ctx.fillStyle = "#e7c17a99"; ctx.fillRect(x + 1, item.y + 16, 7, 2); ctx.fillRect(x + 1, item.y + item.h - 17, 7, 2);
        }
        ctx.fillStyle = "#d1a36a88"; ctx.fillRect(item.x + 5, item.y + item.h - 8, item.w - 10, 3);
      } else if (item.kind === "statue") {
        const cx = item.x + item.w / 2;
        const stone = ctx.createLinearGradient(cx - 20, 0, cx + 20, 0);
        stone.addColorStop(0, "#4f636f"); stone.addColorStop(.4, "#d5d6c3"); stone.addColorStop(1, "#66777b");
        ctx.fillStyle = stone; this.roundRect(item.x + 8, item.y + 67, item.w - 16, 22, 3); ctx.fill();
        ctx.beginPath(); ctx.moveTo(cx - 19, item.y + 65); ctx.quadraticCurveTo(cx - 22, item.y + 38, cx, item.y + 39); ctx.quadraticCurveTo(cx + 22, item.y + 38, cx + 19, item.y + 65); ctx.fill();
        ctx.beginPath(); ctx.ellipse(cx, item.y + 29, 13, 18, 0, 0, TAU); ctx.fill();
        ctx.fillStyle = "#344452"; ctx.fillRect(cx - 8, item.y + 26, 5, 3); ctx.fillRect(cx + 3, item.y + 26, 5, 3);
      } else if (item.kind === "piano") {
        ctx.fillStyle = "#10131f"; this.roundRect(item.x + 7, item.y + 7, item.w - 14, item.h - 30, 8); ctx.fill();
        ctx.strokeStyle = "#bd965a88"; ctx.beginPath(); ctx.moveTo(item.x + 14, item.y + 15); ctx.lineTo(item.x + item.w - 14, item.y + item.h - 36); ctx.stroke();
        const keyWidth = (item.w - 16) / 12;
        for (let key = 0; key < 12; key++) {
          const kx = item.x + 8 + key * keyWidth;
          ctx.fillStyle = "#eadfbe"; ctx.fillRect(kx, item.y + item.h - 25, keyWidth - 1, 17);
          if (![2, 6, 9].includes(key)) { ctx.fillStyle = "#12151e"; ctx.fillRect(kx + keyWidth - 3, item.y + item.h - 25, 4, 10); }
        }
      } else if (item.kind === "sofa") {
        ctx.fillStyle = "#975f7390"; this.roundRect(item.x + 8, item.y + 7, item.w - 16, 15, 6); ctx.fill();
        for (let cushion = 0; cushion < 2; cushion++) {
          const sx = item.x + 15 + cushion * (item.w - 30) / 2;
          ctx.fillStyle = cushion ? "#684052" : "#805064"; this.roundRect(sx, item.y + 26, (item.w - 34) / 2, item.h - 35, 6); ctx.fill();
          ctx.fillStyle = "#d3a28488"; ctx.beginPath(); ctx.arc(sx + (item.w - 34) / 4, item.y + 43, 2, 0, TAU); ctx.fill();
        }
        ctx.fillStyle = "#483240"; this.roundRect(item.x + 4, item.y + 20, 10, item.h - 25, 4); ctx.fill(); this.roundRect(item.x + item.w - 14, item.y + 20, 10, item.h - 25, 4); ctx.fill();
      } else if (item.kind === "table") {
        ctx.strokeStyle = "#cda36655"; ctx.lineWidth = 1; this.roundRect(item.x + 7, item.y + 7, item.w - 14, item.h - 14, 19); ctx.stroke();
        ctx.fillStyle = "#a585653f"; ctx.fillRect(item.x + item.w * .35, item.y + 4, item.w * .3, item.h - 8);
        const cx = item.x + item.w / 2; const cy = item.y + item.h / 2;
        ctx.fillStyle = "#d4b777"; ctx.beginPath(); ctx.ellipse(cx, cy + 5, 13, 6, 0, 0, TAU); ctx.fill();
        ctx.fillStyle = "#f4db9d"; ctx.fillRect(cx - 3, cy - 12, 6, 17);
        const glow = ctx.createRadialGradient(cx, cy - 15, 0, cx, cy - 15, 48);
        glow.addColorStop(0, "#ffcb6266"); glow.addColorStop(1, "#ffcb6200"); ctx.fillStyle = glow; ctx.fillRect(cx - 48, cy - 63, 96, 96);
        ctx.fillStyle = "#fff2bb"; ctx.beginPath(); ctx.ellipse(cx, cy - 16, 3, 7, .15, 0, TAU); ctx.fill();
      }
      ctx.restore();
    });
  }

  private drawBatteries(time: number) {
    const ctx = this.ctx;
    this.batteries.forEach((battery) => {
      const bob = Math.sin(time * 2.8 + battery.phase) * 5;
      ctx.save();
      ctx.translate(battery.x, battery.y + bob);
      ctx.globalCompositeOperation = "screen";
      const glow = ctx.createRadialGradient(0, 0, 2, 0, 0, 55);
      glow.addColorStop(0, "rgba(255,232,112,.55)");
      glow.addColorStop(1, "rgba(255,205,60,0)");
      ctx.fillStyle = glow; ctx.beginPath(); ctx.arc(0, 0, 55, 0, TAU); ctx.fill();
      ctx.globalCompositeOperation = "source-over";
      ctx.shadowColor = "#ffe46f"; ctx.shadowBlur = 15;
      ctx.fillStyle = "#24202a"; ctx.strokeStyle = "#ffe17a"; ctx.lineWidth = 3;
      this.roundRect(-19, -13, 38, 26, 6); ctx.fill(); ctx.stroke();
      ctx.fillStyle = "#ffe17a"; this.roundRect(-13, -7, 26, 14, 3); ctx.fill();
      ctx.fillRect(19, -5, 6, 10);
      ctx.fillStyle = "#4e4018"; ctx.fillRect(-2, -5, 4, 10); ctx.fillRect(-5, -2, 10, 4);
      ctx.shadowBlur = 0; ctx.fillStyle = "rgba(255,244,184,.8)"; ctx.font = "900 8px system-ui"; ctx.textAlign = "center"; ctx.fillText("BATTERY", 0, 28);
      ctx.restore();
    });
  }

  private drawHunter(hunter: Hunter, time: number) {
    const ctx = this.ctx;
    ctx.save(); ctx.translate(hunter.x, hunter.y); ctx.rotate(hunter.facing);
    ctx.scale(1.08, 1.08 + Math.sin(time * 2.5 + hunter.id) * .018);
    if (hunter.down) { ctx.rotate(Math.PI / 2); ctx.globalAlpha = .55; }
    ctx.fillStyle = "#0008"; ctx.beginPath(); ctx.ellipse(-3, 4, 25, 27, 0, 0, TAU); ctx.fill();
    ctx.fillStyle = "#151a26";
    this.roundRect(-16, -22, 18, 12, 5); ctx.fill(); this.roundRect(-16, 10, 18, 12, 5); ctx.fill();
    const coat = ctx.createLinearGradient(-20, -20, 17, 20);
    coat.addColorStop(0, hunter.color); coat.addColorStop(.45, "#344b5f"); coat.addColorStop(1, "#121e30");
    ctx.fillStyle = coat; ctx.strokeStyle = hunter.color; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.ellipse(-3, 0, 18, 23, 0, 0, TAU); ctx.fill(); ctx.stroke();
    ctx.fillStyle = "#172735"; this.roundRect(-22, -12, 11, 24, 4); ctx.fill();
    ctx.strokeStyle = "#829898"; ctx.lineWidth = 1; this.roundRect(-23, -10, 9, 20, 3); ctx.stroke();
    ctx.fillStyle = "#d6b66e"; ctx.fillRect(-20, -4, 3, 8);
    ctx.strokeStyle = "#dfd2ac88"; ctx.beginPath(); ctx.moveTo(-12, -16); ctx.lineTo(1, 17); ctx.stroke();
    const face = ctx.createRadialGradient(9, -5, 1, 6, 0, 15);
    face.addColorStop(0, "#fff0cb"); face.addColorStop(1, "#b58668"); ctx.fillStyle = face;
    ctx.beginPath(); ctx.ellipse(7, 0, 13, 15, 0, 0, TAU); ctx.fill();
    ctx.fillStyle = hunter.color; ctx.beginPath(); ctx.ellipse(0, 0, 9, 17, 0, Math.PI / 2, Math.PI * 1.5); ctx.fill();
    ctx.strokeStyle = "#edffe57a"; ctx.lineWidth = 2; ctx.beginPath(); ctx.ellipse(-1, 0, 8, 14, 0, Math.PI * .6, Math.PI * 1.4); ctx.stroke();
    ctx.fillStyle = hunter.color; this.roundRect(1, -17, 7, 34, 3); ctx.fill();
    ctx.fillStyle = "#10151e"; ctx.beginPath(); ctx.arc(13, -6, 2.5, 0, TAU); ctx.arc(13, 6, 2.5, 0, TAU); ctx.fill();
    ctx.fillStyle = "#f4dbb9"; ctx.beginPath(); ctx.arc(16, 17, 6, 0, TAU); ctx.fill();
    ctx.fillStyle = "#2b3441"; ctx.strokeStyle = "#b2bec7"; this.roundRect(20, 9, 20, 13, 3); ctx.fill(); ctx.stroke();
    ctx.fillStyle = hunter.color; ctx.fillRect(33, 9, 3, 13);
    ctx.shadowColor = hunter.color; ctx.shadowBlur = hunter.pulse ? 16 : 4;
    ctx.fillStyle = hunter.pulse ? "#fffde3" : "#9babaa"; ctx.fillRect(39, 10, 4, 11);
    ctx.restore();
    ctx.save();
    ctx.fillStyle = "rgba(0,0,0,.65)"; this.roundRect(hunter.x - 28, hunter.y - 42, 56, 7, 4); ctx.fill();
    ctx.fillStyle = hunter.health > 35 ? hunter.color : "#ff536d"; this.roundRect(hunter.x - 28, hunter.y - 42, 56 * hunter.health / 100, 7, 4); ctx.fill();
    ctx.fillStyle = "rgba(0,0,0,.7)"; this.roundRect(hunter.x - 28, hunter.y - 31, 56, 5, 3); ctx.fill();
    ctx.fillStyle = hunter.battery > 25 ? "#ffe273" : "#ff704f"; this.roundRect(hunter.x - 28, hunter.y - 31, 56 * hunter.battery / 100, 5, 3); ctx.fill();
    if (hunter.down) {
      ctx.strokeStyle = hunter.color; ctx.lineWidth = 5; ctx.beginPath(); ctx.arc(hunter.x, hunter.y, 34, -Math.PI / 2, -Math.PI / 2 + TAU * hunter.revive / 2.2); ctx.stroke();
      ctx.fillStyle = "#fff"; ctx.font = "900 12px system-ui"; ctx.textAlign = "center"; ctx.fillText("REVIVE", hunter.x, hunter.y - 53);
    }
    if (hunter.human && !hunter.down) {
      ctx.fillStyle = hunter.color; ctx.globalAlpha = .7 + Math.sin(time * 5) * .2; ctx.beginPath(); ctx.moveTo(hunter.x, hunter.y - 54); ctx.lineTo(hunter.x - 8, hunter.y - 67); ctx.lineTo(hunter.x + 8, hunter.y - 67); ctx.closePath(); ctx.fill();
    }
    ctx.restore();
  }

  private drawMoveTarget(time: number) {
    if (!this.moveTarget || this.phase !== "playing") return;
    const ctx = this.ctx;
    const pulse = 1 + Math.sin(time * 6) * .12;
    ctx.save();
    ctx.translate(this.moveTarget.x, this.moveTarget.y);
    ctx.scale(pulse, pulse);
    ctx.strokeStyle = this.role === "ghost" ? "#b47aff" : hunterColors[0];
    ctx.globalAlpha = .78;
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.arc(0, 0, 17, 0, TAU);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(-25, 0); ctx.lineTo(-11, 0);
    ctx.moveTo(25, 0); ctx.lineTo(11, 0);
    ctx.moveTo(0, -25); ctx.lineTo(0, -11);
    ctx.moveTo(0, 25); ctx.lineTo(0, 11);
    ctx.stroke();
    ctx.restore();
  }

  private drawGhost(time: number) {
    const ctx = this.ctx;
    const visible = this.role === "ghost" || this.phase === "setup" || this.ghost.visible > 0;
    if (!visible && distance(this.ghost, this.hunters.find((hunter) => hunter.human) || { x: 0, y: 0 }) > 100) return;
    ctx.save(); ctx.translate(this.ghost.x, this.ghost.y + Math.sin(time * 3.2) * 6);
    ctx.globalAlpha = this.role === "ghost" ? .9 : this.ghost.visible > 0 ? clamp(this.ghost.visible * 3, .22, .92) : .12;
    ctx.globalCompositeOperation = "screen";
    ctx.shadowColor = "#a268ff"; ctx.shadowBlur = 28;
    // All aura details share the ghost's existing visibility gate and opacity.
    const aura = ctx.createRadialGradient(0, -8, 14, 0, -8, 74);
    aura.addColorStop(0, "#9663e53b"); aura.addColorStop(1, "#7443df00"); ctx.fillStyle = aura;
    ctx.beginPath(); ctx.ellipse(0, -8, 74, 65, 0, 0, TAU); ctx.fill();
    ctx.strokeStyle = "#ae9bff55"; ctx.lineWidth = 2;
    for (let wisp = 0; wisp < 3; wisp++) {
      const wx = (wisp - 1) * 18;
      ctx.beginPath(); ctx.moveTo(wx, 18); ctx.bezierCurveTo(wx + 14, 36, wx - 10 + Math.sin(time * 3 + wisp) * 12, 45, wx + Math.sin(time * 2 + wisp) * 16, 58); ctx.stroke();
    }
    const grad = ctx.createRadialGradient(0, -8, 3, 0, 5, 48);
    grad.addColorStop(0, "#fffbff"); grad.addColorStop(.3, "#d9beff"); grad.addColorStop(.75, "#8f62d0b8"); grad.addColorStop(1, "#6c36a425");
    ctx.fillStyle = grad;
    ctx.beginPath(); ctx.moveTo(-38, 18); ctx.bezierCurveTo(-44,-34,-22,-52,0,-52); ctx.bezierCurveTo(26,-52,45,-27,38,18); ctx.quadraticCurveTo(28,7,18,28); ctx.quadraticCurveTo(5,8,-6,31); ctx.quadraticCurveTo(-19,9,-38,18); ctx.fill();
    ctx.strokeStyle = "#dacaff99"; ctx.lineWidth = 2; ctx.stroke();
    ctx.fillStyle = "#ffffff20"; ctx.beginPath(); ctx.ellipse(-14, -29, 10, 15, .55, 0, TAU); ctx.fill();
    ctx.strokeStyle = "#bea2ff66"; ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(-31, -6); ctx.quadraticCurveTo(-54, -15 + Math.sin(time * 4) * 5, -45, 9); ctx.moveTo(31, -6); ctx.quadraticCurveTo(54, -15 - Math.sin(time * 4) * 5, 45, 9); ctx.stroke();
    ctx.fillStyle = "#0c0920"; ctx.shadowBlur = 0; ctx.beginPath(); ctx.ellipse(-12,-18,7,11,-.2,0,TAU); ctx.ellipse(14,-18,7,11,.2,0,TAU); ctx.fill();
    ctx.fillStyle = "#f0e1ff"; ctx.beginPath(); ctx.ellipse(-10,-21,2,3,-.2,0,TAU); ctx.ellipse(16,-21,2,3,.2,0,TAU); ctx.fill();
    ctx.strokeStyle = "#251342"; ctx.lineWidth = 4; ctx.beginPath(); ctx.arc(2, 1, 14, .22, Math.PI - .15); ctx.stroke();
    ctx.restore();
    if (visible) {
      ctx.fillStyle = "rgba(0,0,0,.65)"; this.roundRect(this.ghost.x - 35, this.ghost.y - 66, 70, 8, 4); ctx.fill();
      ctx.fillStyle = "#b47aff"; this.roundRect(this.ghost.x - 35, this.ghost.y - 66, 70 * this.ghost.energy / 100, 8, 4); ctx.fill();
    }
  }

  private drawParticles() {
    const ctx = this.ctx;
    ctx.save(); ctx.globalCompositeOperation = "screen";
    this.particles.forEach((particle) => {
      ctx.globalAlpha = clamp(particle.life * 1.4, 0, 1); ctx.fillStyle = particle.color; ctx.shadowColor = particle.color; ctx.shadowBlur = 8;
      ctx.beginPath(); ctx.arc(particle.x, particle.y, particle.size, 0, TAU); ctx.fill();
    });
    ctx.restore();
  }

  private drawCountdown() {
    const ctx = this.ctx;
    const number = Math.ceil(this.countdown);
    ctx.save(); ctx.fillStyle = "rgba(3,5,14,.48)"; ctx.fillRect(0, 0, W, H);
    ctx.textAlign = "center"; ctx.fillStyle = "#fff4ce"; ctx.shadowColor = "#8afdf3"; ctx.shadowBlur = 30; ctx.font = "900 180px system-ui"; ctx.fillText(number > 0 ? String(number) : "GO", W / 2, H / 2 + 65); ctx.restore();
  }

  private updateHud() {
    const totalSeconds = Math.ceil(this.remaining);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = (totalSeconds % 60).toString().padStart(2, "0");
    const time = this.root.querySelector("#gm-time"); if (time) time.textContent = `${minutes}:${seconds}`;
    const energy = this.root.querySelector<HTMLElement>("#gm-ghost-energy"); if (energy) energy.style.width = `${this.ghost.energy}%`;
    const label = this.root.querySelector("#gm-energy-label"); if (label) label.textContent = String(Math.ceil(this.ghost.energy));
    const pips = this.root.querySelector("#gm-hunter-pips");
    if (pips) pips.innerHTML = this.hunters.map((hunter) => `<i style="--hunter:${hunter.color};--health:${hunter.health}%" class="${hunter.down ? "is-down" : ""}"><b></b><span>${hunter.id + 1}</span></i>`).join("");
    this.syncFlashlightControl();
  }

  private showAlert(title: string, subtitle: string) {
    this.alertEndsAt = performance.now() + 2700;
    const alert = this.root.querySelector<HTMLElement>("#gm-alert")!;
    alert.innerHTML = `<strong>${title}</strong><span>${subtitle}</span>`;
    alert.classList.remove("is-showing"); void alert.offsetWidth; alert.classList.add("is-showing");
  }

  private pointerStart(event: PointerEvent) {
    if (this.phase !== "playing") return;
    if ((event.target as HTMLElement).closest("button")) return;
    if (event.pointerType === "mouse") {
      const target = this.worldPointFromPointer(event);
      if (target) this.moveTarget = target;
      return;
    }
    if (!this.usesTouchControls()) return;
    event.preventDefault();
    (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
    this.pointer.active = true;
    this.pointerMove(event);
  }

  private worldPointFromPointer(event: PointerEvent): Vec | null {
    const rect = this.canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const canvasX = (event.clientX - rect.left) * this.canvas.width / rect.width;
    const canvasY = (event.clientY - rect.top) * this.canvas.height / rect.height;
    const scale = Math.min(this.canvas.width / W, this.canvas.height / H);
    const offsetX = (this.canvas.width - W * scale) / 2;
    const offsetY = (this.canvas.height - H * scale) / 2;
    return {
      x: clamp((canvasX - offsetX) / scale, 28, W - 28),
      y: clamp((canvasY - offsetY) / scale, 28, H - 28),
    };
  }

  private pointerMove(event: PointerEvent) {
    if (!this.pointer.active) return;
    const rect = this.canvas.getBoundingClientRect();
    const originX = rect.left + Math.min(110, rect.width * .18);
    const originY = rect.bottom - Math.min(110, rect.height * .2);
    const dx = event.clientX - originX;
    const dy = event.clientY - originY;
    const length = Math.max(1, Math.hypot(dx, dy));
    this.pointer.x = dx / Math.max(48, length);
    this.pointer.y = dy / Math.max(48, length);
    const stick = this.root.querySelector<HTMLElement>("#gm-stick i")!;
    stick.style.transform = `translate(${this.pointer.x * 32}px,${this.pointer.y * 32}px)`;
  }

  private pointerEnd() {
    this.pointer.active = false; this.pointer.x = 0; this.pointer.y = 0; this.pointer.action = false;
    const stick = this.root.querySelector<HTMLElement>("#gm-stick i"); if (stick) stick.style.transform = "translate(0,0)";
  }

  private resize() {
    const rect = this.canvas.parentElement!.getBoundingClientRect();
    const ratio = Math.min(this.usesTouchControls() ? 1 : 1.25, devicePixelRatio || 1);
    this.canvas.width = Math.max(1, Math.floor(rect.width * ratio));
    this.canvas.height = Math.max(1, Math.floor(rect.height * ratio));
  }

  private usesTouchControls() {
    return navigator.maxTouchPoints > 0 || matchMedia("(pointer: coarse)").matches;
  }

  private seedMotes() {
    for (let i = 0; i < 85; i++) this.motes.push({ ...randomPoint(), phase: Math.random() * TAU, speed: .4 + Math.random() * 1.3 });
  }

  private spark(x: number, y: number, color: string) {
    this.particles.push({ x, y, vx: (Math.random() - .5) * 90, vy: (Math.random() - .5) * 90, life: .3 + Math.random() * .35, color, size: 1 + Math.random() * 3 });
  }

  private burst(point: Vec, color: string, count: number) {
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * TAU; const speed = 35 + Math.random() * 190;
      this.particles.push({ x: point.x, y: point.y, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed, life: .4 + Math.random() * .8, color, size: 1 + Math.random() * 4 });
    }
  }

  private roundRect(x: number, y: number, w: number, h: number, radius: number) {
    this.ctx.beginPath(); this.ctx.roundRect(x, y, w, h, radius);
  }

  private unlockAudio() {
    if (!this.audio) this.audio = new AudioContext();
    void this.audio.resume();
  }

  private tone(frequency: number, duration: number, type: OscillatorType, volume = .025) {
    if (!this.sound) return;
    this.unlockAudio();
    const oscillator = this.audio!.createOscillator(); const gain = this.audio!.createGain();
    oscillator.type = type; oscillator.frequency.value = frequency; gain.gain.setValueAtTime(volume, this.audio!.currentTime); gain.gain.exponentialRampToValueAtTime(.0001, this.audio!.currentTime + duration);
    oscillator.connect(gain); gain.connect(this.audio!.destination); oscillator.start(); oscillator.stop(this.audio!.currentTime + duration);
  }
}

export function mountGhostMansion(root: HTMLElement, onExit: () => void, voiceOptions: MansionVoiceOptions) {
  const game = new MansionGame(root, onExit, voiceOptions);
  return () => game.destroy();
}

export function spectralManorPlayers(role: Role, localPlayers: number, names: string[]): SpectralManorResult["players"] {
  return [
    ...Array.from({ length: 3 }, (_, index) => ({ name: role === "hunter" && index < localPlayers ? names[index].trim() : `Manor Keeper ${index + 1} (AI)`, team: "hunters" as const })),
    { name: role === "ghost" ? names[0].trim() : "Manor Phantom (AI)", team: "phantom" as const },
  ];
}
